import { createElement, createRef, Ref, Suspense } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { MessageToast } from '.';
import { OmitStrict } from '../../utility-types';
import { MessageToastProps } from './';
import { getGlobalSettingsService, GlobalSettingsKeys } from '../Settings';
import { useThemeContext, ThemeProvider, useGlobalDesignTokens } from '../../theme';
import { WritingModeProvider, useWritingModeContext } from '../../locale';
import cn from './MessageToaster.css.json';
import './MessageToaster.css';

interface OpenToast {
  container: HTMLElement;
  root: Root;
  timeout: number;
  hovering: boolean;
  toast: HTMLDivElement | null;
  hideAnimation: boolean;
}

let openToast: OpenToast | null = null;
let toastId = 0;
const DEFAULT_DURATION = 5;

// There needs to be a discussion of how pop over content is handled globally.
// For now, we make sure there is some place to put the toasts.
function getStaticArea() {
  let staticArea = document.getElementById('static-area');
  if (!staticArea) {
    staticArea = document.createElement('div');
    staticArea.id = 'static-area';
    staticArea.className = cn.staticArea;
    document.body.appendChild(staticArea);
  }
  return staticArea;
}

function createToastContainer() {
  toastId++;

  const toastContainer = document.createElement('div');
  toastContainer.id = `toast-${toastId}`;
  toastContainer.setAttribute('role', 'alert');
  getStaticArea().appendChild(toastContainer);

  return toastContainer;
}

function getToastDuration() {
  const settingEnabled = getGlobalSettingsService().get(
    GlobalSettingsKeys.ENABLE_POPUP_DISPLAY_DURATION
  );
  const setting =
    settingEnabled === 'true'
      ? getGlobalSettingsService().get(GlobalSettingsKeys.POPUP_DISPLAY_DURATION)
      : undefined;
  return (setting ? parseFloat(setting) : DEFAULT_DURATION) * 1000;
}

function clearToast(toast: OpenToast | null) {
  if (!toast) {
    return;
  }

  clearTimeout(toast.timeout);
  const staticArea = getStaticArea();
  if (staticArea.contains(toast.container)) {
    const animationEndHandler = () => {
      toast.root.unmount();
      staticArea.removeChild(toast.container);
      toast.toast?.removeEventListener('animationend', animationEndHandler);
    };
    const closingAnimation = toast.toast?.dataset.closingAnim;

    if (toast.hideAnimation || !closingAnimation || closingAnimation === 'false') {
      animationEndHandler();
    } else {
      toast.toast?.addEventListener('animationend', animationEndHandler);
      toast.toast?.classList.add(closingAnimation);
    }
  }

  if (toast === openToast) {
    openToast = null;
  }
}

export interface ShowMessageToastOptions
  extends OmitStrict<MessageToastProps, 'children' | 'onMouseEnter' | 'onMouseLeave' | 'isOpen'> {}

export type ShowMessageToastType = (message: string, options?: ShowMessageToastOptions) => void;
/**
 * Imperatively renders a Toast on the page. The toast will close itself.
 *
 * @param message The message to display in a toast.
 * @param options
 * @param options.closeable When true the toast will display a close button.
 * @param options.displayMode A string the specifies a predefined style to use for the toast.
 */

export const useShowMessageToast = (): ShowMessageToastType => {
  const themeContext = useThemeContext();
  const globalDesignTokens = useGlobalDesignTokens();
  const writingMode = useWritingModeContext();

  return function showMessageToast(message: string, options?: ShowMessageToastOptions) {
    const container = createToastContainer();
    clearToast(openToast);

    const actionButtonRef: Ref<HTMLButtonElement> = createRef();
    const actionButtonProps = options?.actionButton;

    const timeout = window.setTimeout(() => {
      // Don't close the toast if we're hovering over it or the action button is focused
      if (thisToast.hovering || actionButtonRef.current === document.activeElement)
        thisToast.timeout = -1;
      else clearToast(thisToast);
    }, getToastDuration());
    const root = createRoot(container);
    const thisToast: OpenToast = {
      container,
      root,
      timeout,
      hovering: false,
      toast: null,
      hideAnimation: options?.hideAnimation ?? false,
    };

    root.render(
      <Suspense fallback={null}>
        <div style={globalDesignTokens}>
          <ThemeProvider {...themeContext}>
            <WritingModeProvider {...writingMode}>
              <MessageToast
                isOpen
                toastRef={(element) => {
                  thisToast.toast = element;
                }}
                {...options}
                actionButton={
                  actionButtonProps
                    ? {
                        ...actionButtonProps,
                        ref: actionButtonRef,
                        onBlur: () => {
                          if (thisToast.timeout === -1) clearToast(thisToast);
                        },
                      }
                    : undefined
                }
                onMouseEnter={() => {
                  thisToast.hovering = true;
                }}
                onMouseLeave={() => {
                  thisToast.hovering = false;
                  if (
                    thisToast.timeout === -1 &&
                    actionButtonRef.current !== document.activeElement
                  )
                    clearToast(thisToast);
                }}
                closeable={options?.closeable}
                onClose={() => {
                  clearToast(thisToast);
                  options?.onClose?.();
                }}
              >
                {message}
              </MessageToast>
            </WritingModeProvider>
          </ThemeProvider>
        </div>
      </Suspense>
    );

    openToast = thisToast;
  };
};
